import { useState, useEffect } from "react";
import type { TemplateField } from "@/types/activity";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Trash2, GripVertical, Code } from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";

function toSnakeCase(str: string): string {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

const FIELD_TYPES = [
  { value: "string", label: "Texte" },
  { value: "integer", label: "Nombre entier" },
  { value: "boolean", label: "Booléen" },
  { value: "select", label: "Liste de choix (select)" },
  { value: "array", label: "Liste" },
  { value: "file", label: "Illustration" },
  { value: "audio", label: "Audio (média)" },
  { value: "video", label: "Vidéo" },
  { value: "transcript", label: "Transcript (audio)" },
  { value: "score_points", label: "Points par option" },
];

const INPUT_TYPES = [
  { value: "text", label: "Texte court" },
  { value: "textarea", label: "Zone de texte" },
  { value: "richtext", label: "Texte riche" },
];

interface FieldEditorProps {
  fields: TemplateField[];
  onChange: (fields: TemplateField[]) => void;
}

export default function CardTemplateFieldEditor({ fields, onChange }: FieldEditorProps) {
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const [jsonError, setJsonError] = useState("");

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const addField = () => {
    onChange([
      ...fields,
      { name: "", label: "", type: "string", required: false },
    ]);
  };

  const updateField = (index: number, updates: Partial<TemplateField>) => {
    const updated = fields.map((f, i) => (i === index ? { ...f, ...updates } : f));
    // Auto-generate name from label if name is empty or was auto-generated
    if (updates.label !== undefined) {
      const old = fields[index];
      if (!old.name || old.name === toSnakeCase(old.label || "")) {
        updated[index] = { ...updated[index], name: toSnakeCase(updates.label) };
      }
    }
    onChange(updated);
  };

  const removeField = (index: number) => {
    onChange(fields.filter((_, i) => i !== index));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = fields.findIndex((_, i) => `field-${i}` === active.id);
    const newIdx = fields.findIndex((_, i) => `field-${i}` === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = [...fields];
    const [moved] = reordered.splice(oldIdx, 1);
    reordered.splice(newIdx, 0, moved);
    onChange(reordered);
  };

  const openJsonDialog = () => {
    setJsonText(JSON.stringify(fields, null, 2));
    setJsonError("");
    setJsonDialogOpen(true);
  };

  const saveJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      if (!Array.isArray(parsed)) {
        setJsonError("Le JSON doit être un tableau de champs.");
        return;
      }
      onChange(parsed);
      setJsonDialogOpen(false);
    } catch {
      setJsonError("JSON invalide.");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Champs du template</Label>
        <div className="flex gap-1">
          <Button variant="outline" size="sm" onClick={openJsonDialog}>
            <Code className="h-3.5 w-3.5 mr-1" /> JSON
          </Button>
          <Button variant="outline" size="sm" onClick={addField}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Champ
          </Button>
        </div>
      </div>

      {fields.length === 0 ? (
        <p className="text-sm text-muted-foreground">Aucun champ défini. Cliquez sur "+ Champ" pour commencer.</p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={fields.map((_, i) => `field-${i}`)} strategy={verticalListSortingStrategy}>
            <Accordion type="multiple" className="space-y-1">
              {fields.map((field, index) => (
                <SortableFieldItem
                  key={`field-${index}`}
                  id={`field-${index}`}
                  field={field}
                  index={index}
                  allFields={fields}
                  onUpdate={(updates) => updateField(index, updates)}
                  onRemove={() => removeField(index)}
                />
              ))}
            </Accordion>
          </SortableContext>
        </DndContext>
      )}

      {/* JSON Editor Dialog */}
      <Dialog open={jsonDialogOpen} onOpenChange={setJsonDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Édition JSON des champs</DialogTitle>
            <DialogDescription>Modifiez le JSON brut des content_fields. Les changements seront appliqués à l'enregistrement.</DialogDescription>
          </DialogHeader>
          <Textarea
            value={jsonText}
            onChange={(e) => { setJsonText(e.target.value); setJsonError(""); }}
            className="font-mono text-xs min-h-[300px]"
          />
          {jsonError && <p className="text-sm text-destructive">{jsonError}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setJsonDialogOpen(false)}>Annuler</Button>
            <Button onClick={saveJson}>Appliquer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─── Sortable Field Item ─────────────────────────── */

function SortableFieldItem({
  id,
  field,
  index,
  allFields,
  onUpdate,
  onRemove,
}: {
  id: string;
  field: TemplateField;
  index: number;
  allFields: TemplateField[];
  onUpdate: (updates: Partial<TemplateField>) => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <AccordionItem value={id} className="border rounded-md px-2">
        <div className="flex items-center gap-1">
          <div {...attributes} {...listeners} className="cursor-grab p-1">
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
          <AccordionTrigger className="flex-1 py-2 text-sm hover:no-underline">
            <span className="truncate">
              {field.label || field.name || `Champ ${index + 1}`}
              <span className="ml-2 text-xs text-muted-foreground">({field.type})</span>
              {field.required && <span className="ml-1 text-destructive">*</span>}
            </span>
          </AccordionTrigger>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={onRemove}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
        <AccordionContent className="px-1 pb-3">
          <FieldConfig field={field} onUpdate={onUpdate} allFields={allFields} />
        </AccordionContent>
      </AccordionItem>
    </div>
  );
}

/* ─── Field Configuration ─────────────────────────── */

function FieldConfig({
  field,
  onUpdate,
  allFields,
}: {
  field: TemplateField;
  onUpdate: (updates: Partial<TemplateField>) => void;
  allFields: TemplateField[];
}) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Label</Label>
          <Input
            value={field.label || ""}
            onChange={(e) => onUpdate({ label: e.target.value })}
            placeholder="Titre du champ"
            className="h-8 text-sm"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Nom (clé JSON)</Label>
          <Input
            value={field.name}
            onChange={(e) => onUpdate({ name: e.target.value })}
            placeholder="nom_du_champ"
            className="h-8 text-sm font-mono"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Type</Label>
          <Select value={field.type} onValueChange={(v) => onUpdate({ type: v })}>
            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {FIELD_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {field.type === "string" && (
          <div className="space-y-1">
            <Label className="text-xs">Input</Label>
            <Select value={field.input || "text"} onValueChange={(v) => onUpdate({ input: v })}>
              <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {INPUT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <Switch
            checked={!!field.required}
            onCheckedChange={(c) => onUpdate({ required: c })}
            id={`req-${field.name}`}
          />
          <Label htmlFor={`req-${field.name}`} className="text-xs">Obligatoire</Label>
        </div>
        {field.type === "string" && (
          <div className="flex items-center gap-2">
            <Switch
              checked={field.format === "uri"}
              onCheckedChange={(c) => onUpdate({ format: c ? "uri" : undefined })}
              id={`uri-${field.name}`}
            />
            <Label htmlFor={`uri-${field.name}`} className="text-xs">URL</Label>
          </div>
        )}
      </div>

      {/* Numeric constraints */}
      {field.type === "integer" && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Minimum</Label>
            <Input
              type="number"
              value={field.minimum ?? ""}
              onChange={(e) => onUpdate({ minimum: e.target.value ? Number(e.target.value) : undefined })}
              className="h-8 text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Maximum</Label>
            <Input
              type="number"
              value={field.maximum ?? ""}
              onChange={(e) => onUpdate({ maximum: e.target.value ? Number(e.target.value) : undefined })}
              className="h-8 text-sm"
            />
          </div>
        </div>
      )}

      {/* String max length */}
      {field.type === "string" && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Longueur max</Label>
            <Input
              type="number"
              value={field.maximum ?? ""}
              onChange={(e) => onUpdate({ maximum: e.target.value ? Number(e.target.value) : undefined })}
              className="h-8 text-sm"
            />
          </div>
        </div>
      )}

      {/* File / Audio / Video bucket config */}
      {(field.type === "file" || field.type === "audio" || field.type === "video") && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Bucket</Label>
            <Input
              value={field.bucket || (field.type === "audio" ? "audio" : field.type === "video" ? "video" : "media")}
              onChange={(e) => onUpdate({ bucket: e.target.value || (field.type === "audio" ? "audio" : field.type === "video" ? "video" : "media") })}
              placeholder={field.type === "audio" ? "audio" : field.type === "video" ? "video" : "media"}
              className="h-8 text-sm font-mono"
            />
          </div>
        </div>
      )}

      {/* Select options */}
      {field.type === "select" && (
        <SelectOptionsEditor field={field} onUpdate={onUpdate} />
      )}

      {/* Default value */}
      {(field.type === "string" || field.type === "integer" || field.type === "boolean" || field.type === "select") && (
        <DefaultValueEditor field={field} onUpdate={onUpdate} />
      )}

      {/* File position option */}
      {field.type === "file" && (
        <div className="flex items-center gap-2">
          <Switch
            checked={!!field.hasPosition}
            onCheckedChange={(c) => onUpdate({ hasPosition: c })}
            id={`pos-${field.name}`}
          />
          <Label htmlFor={`pos-${field.name}`} className="text-xs">Choix de position</Label>
        </div>
      )}

      {/* Array config */}
      {field.type === "array" && (
        <ArrayFieldConfig field={field} onUpdate={onUpdate} />
      )}

      {/* Score points: linked field selector */}
      {field.type === "score_points" && (
        <div className="space-y-1">
          <Label className="text-xs">Champ lié (tableau d'options)</Label>
          <Select
            value={field.linkedField || "__none__"}
            onValueChange={(v) => onUpdate({ linkedField: v === "__none__" ? undefined : v })}
          >
            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— Aucun —</SelectItem>
              {allFields
                .filter((f) => f.type === "array" && f.name !== field.name)
                .map((f) => (
                  <SelectItem key={f.name} value={f.name}>{f.label || f.name}</SelectItem>
                ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Sélectionner le champ de type liste contenant les options auxquelles attribuer des points.
          </p>
        </div>
      )}
    </div>
  );
}

/* ─── Default value editor ─────────────────────────── */

function DefaultValueEditor({
  field,
  onUpdate,
}: {
  field: TemplateField;
  onUpdate: (updates: Partial<TemplateField>) => void;
}) {
  const hasDefault = field.defaultValue !== undefined && field.defaultValue !== null && field.defaultValue !== "";

  if (field.type === "boolean") {
    return (
      <div className="flex items-center gap-2">
        <Switch
          checked={!!field.defaultValue}
          onCheckedChange={(c) => onUpdate({ defaultValue: c })}
          id={`def-${field.name}`}
        />
        <Label htmlFor={`def-${field.name}`} className="text-xs">Valeur par défaut (activé)</Label>
      </div>
    );
  }

  if (field.type === "select") {
    const options = field.options || [];
    return (
      <div className="space-y-1">
        <Label className="text-xs">Valeur par défaut</Label>
        <Select
          value={(field.defaultValue as string) || "__none__"}
          onValueChange={(v) => onUpdate({ defaultValue: v === "__none__" ? undefined : v })}
        >
          <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— Aucune —</SelectItem>
            {options.map((opt) => {
              const key = typeof opt === "string" ? opt : opt.key;
              const label = typeof opt === "string" ? opt : opt.label;
              return <SelectItem key={key} value={key}>{label}</SelectItem>;
            })}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (field.type === "integer") {
    return (
      <div className="space-y-1">
        <Label className="text-xs">Valeur par défaut</Label>
        <Input
          type="number"
          value={field.defaultValue !== undefined && field.defaultValue !== null ? String(field.defaultValue) : ""}
          onChange={(e) => onUpdate({ defaultValue: e.target.value ? Number(e.target.value) : undefined })}
          placeholder="Non définie"
          className="h-8 text-sm"
        />
      </div>
    );
  }

  // string
  return (
    <div className="space-y-1">
      <Label className="text-xs">Valeur par défaut</Label>
      <Input
        value={(field.defaultValue as string) || ""}
        onChange={(e) => onUpdate({ defaultValue: e.target.value || undefined })}
        placeholder="Non définie"
        className="h-8 text-sm"
      />
    </div>
  );
}

/* ─── Select options editor (stores raw text, parses on blur) ── */

function SelectOptionsEditor({
  field,
  onUpdate,
}: {
  field: TemplateField;
  onUpdate: (updates: Partial<TemplateField>) => void;
}) {
  const serialize = (opts: typeof field.options) =>
    (opts || [])
      .map((opt) => (typeof opt === "string" ? opt : `${opt.key}:${opt.label}`))
      .join("\n");

  const [raw, setRaw] = useState(() => serialize(field.options));

  useEffect(() => {
    setRaw(serialize(field.options));
  }, [field.options]);

  const commitOptions = (text: string) => {
    const opts = text
      .split("\n")
      .filter((l) => l.trim() !== "")
      .map((line) => {
        const colonIdx = line.indexOf(":");
        if (colonIdx > 0) {
          return { key: line.slice(0, colonIdx).trim(), label: line.slice(colonIdx + 1).trim() };
        }
        return line.trim();
      });
    onUpdate({ options: opts });
  };

  return (
    <div className="space-y-2">
      <Label className="text-xs">Options (une par ligne, format clé:label ou juste clé)</Label>
      <Textarea
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        onBlur={() => commitOptions(raw)}
        placeholder={"title-description:Titre + Description\nindicator:Indicateur"}
        rows={4}
        className="font-mono text-xs"
      />
      <div className="flex items-center gap-2">
        <Switch
          checked={!!field.controlsVisibility}
          onCheckedChange={(c) => onUpdate({ controlsVisibility: c })}
          id={`vis-${field.name}`}
        />
        <Label htmlFor={`vis-${field.name}`} className="text-xs">Contrôle la visibilité des champs</Label>
      </div>
      {field.controlsVisibility && (
        <p className="text-xs text-muted-foreground">
          La clé détermine les champs affichés. Ex : <code className="bg-muted px-1 rounded">title-description</code> affiche les champs <code className="bg-muted px-1 rounded">title</code> et <code className="bg-muted px-1 rounded">description</code>.
        </p>
      )}
    </div>
  );
}

/* ─── Array sub-fields config ─────────────────────── */

function ArrayFieldConfig({
  field,
  onUpdate,
}: {
  field: TemplateField;
  onUpdate: (updates: Partial<TemplateField>) => void;
}) {
  const itemType = field.item?.type || "string";
  const subFields = field.item?.fields || [];

  const setItemType = (type: string) => {
    if (type === "object") {
      onUpdate({ item: { type: "object", fields: subFields.length > 0 ? subFields : [] } });
    } else {
      onUpdate({ item: { type, fields: [] } });
    }
  };

  const updateSubFields = (fields: TemplateField[]) => {
    onUpdate({ item: { type: "object", fields } });
  };

  return (
    <div className="space-y-3 pl-3 border-l-2 border-primary/20">
      <div className="flex items-center gap-2 mb-2">
        <Switch
          checked={!!field.hasTypology}
          onCheckedChange={(c) => onUpdate({ hasTypology: c })}
          id={`typology-${field.name}`}
        />
        <Label htmlFor={`typology-${field.name}`} className="text-xs">Typologie Positif / Négatif sur les items</Label>
      </div>
      <div className="flex items-center gap-2 mb-2">
        <Switch
          checked={!!field.hasCustomItems}
          onCheckedChange={(c) => onUpdate({ hasCustomItems: c })}
          id={`custom-${field.name}`}
        />
        <Label htmlFor={`custom-${field.name}`} className="text-xs">Permettre des options personnalisables (is_custom)</Label>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Min. éléments</Label>
          <Input
            type="number"
            value={field.minItems ?? ""}
            onChange={(e) => onUpdate({ minItems: e.target.value ? Number(e.target.value) : undefined })}
            className="h-8 text-sm"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Type d'élément</Label>
          <Select value={itemType} onValueChange={setItemType}>
            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="string">Texte</SelectItem>
              <SelectItem value="integer">Nombre</SelectItem>
              <SelectItem value="boolean">Booléen</SelectItem>
              <SelectItem value="object">Objet</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {itemType === "object" && (
        <div className="space-y-2">
          <Label className="text-xs font-medium">Sous-champs de l'objet</Label>
          <CardTemplateFieldEditor fields={subFields} onChange={updateSubFields} />
        </div>
      )}
    </div>
  );
}
